import React, { useState } from "react";
import UserInfoForm from "@/components/contractor/UserInfoForm";
import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { CheckCircle } from "lucide-react";
import testimonial1 from "@/images/testimonial1.webp";
import testimonial2 from "@/images/testimonial2.webp";
import PublicNavBar from "@/components/layout/PublicNavBar";
import PublicFooter from "@/components/layout/PublicFooter";

interface Question {
  id: number;
  text: string;
  contractorOption: string;
  employeeOption: string;
}

const QUESTIONS: Question[] = [
  {
    id: 1,
    text: "Why are you bringing this nanny on?",
    contractorOption:
      "Hired for their own specialist skills, reputation or niche services",
    employeeOption:
      "Needed to handle ordinary, day-to-day childcare tasks for the family",
  },
  {
    id: 2,
    text: "Who sets the nanny's working days & hours?",
    contractorOption:
      "Nanny chooses when to work (agreed deadlines / parents just need cover)",
    employeeOption:
      "Parents decide the exact schedule (start, finish and break times)",
  },
  {
    id: 3,
    text: "Can the nanny send a qualified substitute if they're ill or double-booked?",
    contractorOption:
      "Yes – they may arrange another vetted nanny and inform the parents",
    employeeOption: "No – the parents expect *that specific* nanny every time",
  },
  {
    id: 4,
    text: "Does the nanny supply their own toys, learning materials or car seats?",
    contractorOption: "Yes – they bring and maintain their own equipment",
    employeeOption: "No – the family provides everything needed for the job",
  },
  {
    id: 5,
    text: "How is pay calculated?",
    contractorOption:
      "A fixed price per job / per booking (e.g. £160 for a weekend trip)",
    employeeOption:
      "A regular hourly or weekly wage, possibly with paid holiday & sick pay",
  },
  {
    id: 6,
    text: "Can earnings go up if the nanny works faster or adds extras (e.g. tutoring)?",
    contractorOption:
      "Yes – the nanny can charge more for extra value or efficiency",
    employeeOption:
      "No – pay is capped except for occasional agreed bonuses or tips",
  },
  {
    id: 7,
    text: "What happens if parents are unhappy with the work?",
    contractorOption:
      "Nanny fixes issues on their own time and at their own expense",
    employeeOption:
      "Parents expect to be paid only once, and corrections are on the clock",
  },
  {
    id: 8,
    text: "Contract length",
    contractorOption:
      "Short-term / fixed-term contract for a specific period or project",
    employeeOption: "Open-ended employment after a short probation period",
  },
  {
    id: 9,
    text: "Ending the relationship",
    contractorOption:
      "Either side can terminate for convenience with the notice in the contract",
    employeeOption: "Notice periods & dismissal rules follow UK employment law",
  },
  {
    id: 10,
    text: "Who decides *how* the work is done?",
    contractorOption: "Nanny plans activities, meals & routines independently",
    employeeOption:
      "Parents give detailed instructions on what, how, where and when",
  },
  {
    id: 11,
    text: "Accepting work",
    contractorOption: "Nanny can accept or decline individual bookings",
    employeeOption: "Parents expect every offered shift to be accepted",
  },
  {
    id: 12,
    text: "Can the nanny work for other families at the same time?",
    contractorOption: "Yes – they're free to take on other clients",
    employeeOption: "No – the contract restricts working for other families",
  },
  {
    id: 13,
    text: "Can the nanny subcontract parts of the job (e.g. cleaning, tutoring)?",
    contractorOption:
      "Yes – subcontracting or bringing helpers is allowed if costs are covered",
    employeeOption: "No – the nanny must personally perform all duties",
  },
  {
    id: 14,
    text: "Does the nanny run their own business entity (Ltd / sole trader) and invoice?",
    contractorOption: "Yes – they invoice as an independent business",
    employeeOption: "No – they're paid through household payroll",
  },
  {
    id: 15,
    text: "Will the family provide benefits (paid holiday, pension, gym, etc.)?",
    contractorOption: "No – nanny arranges their own benefits",
    employeeOption: "Yes – family provides statutory or additional benefits",
  },
  {
    id: 16,
    text: "Previous or ongoing series of contracts with the same family?",
    contractorOption:
      "First or isolated contract – each arrangement is separate",
    employeeOption:
      "One of several continuous or rolling contracts with the same family",
  },
  {
    id: 17,
    text: "Is the nanny prevented from working for competing agencies/families?",
    contractorOption: "No – they're free to work elsewhere",
    employeeOption:
      "Yes – the agreement restricts similar work with other clients",
  },
];

type EmploymentStatus = "Contractor" | "Employee";

interface Answers {
  [key: number]: EmploymentStatus;
}

interface UserInfo {
  firstName: string;
  lastName: string;
  email: string;
}

interface QuizResult {
  submissionId: string;
  classification: string;
  error?: string;
}

// Define the expected API response structure
interface QuizSubmissionResponse {
  success: boolean;
  submission_id: string;
  classification: string;
  error?: string;
}

const ContractorQuiz: React.FC = () => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [showInfoForm, setShowInfoForm] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionComplete, setSubmissionComplete] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [quizResult, setQuizResult] = useState<QuizResult | null>(null);
  const [showIntro, setShowIntro] = useState(true);

  const handleAnswer = (questionId: number, answer: EmploymentStatus) => {
    setAnswers((prevAnswers) => ({ ...prevAnswers, [questionId]: answer }));
    if (currentQuestionIndex < QUESTIONS.length - 1) {
      setCurrentQuestionIndex((prevIndex) => prevIndex + 1);
    } else {
      setShowInfoForm(true);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prevIndex) => prevIndex - 1);
    }
  };

  const handleRestartQuiz = () => {
    setCurrentQuestionIndex(0);
    setAnswers({});
    setShowInfoForm(false);
    setUserInfo(null);
    setIsSubmitting(false);
    setSubmissionComplete(false);
    setErrorMessage(null);
    setQuizResult(null);
    setShowIntro(true);
  };

  const handleUserInfoSubmit = async (info: UserInfo) => {
    setUserInfo(info);
    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      // Prepare data for API submission
      const requestData = {
        first_name: info.firstName,
        last_name: info.lastName,
        email: info.email,
        answers: answers,
      };

      // Make API request to submit quiz using the correct ApiService
      const response = await ApiService.request<QuizSubmissionResponse>(
        EndpointKeys.SUBMIT_CONTRACTOR_QUIZ,
        {
          method: "POST",
          body: requestData,
          skipAuth: true, // Skip authentication for public quiz
        },
      );

      if (response.success) {
        // Update state with the response
        setQuizResult({
          submissionId: response.submission_id,
          classification: response.classification,
        });

        setSubmissionComplete(true);
      } else {
        throw new Error(response.error || "Failed to submit quiz");
      }
    } catch (error) {
      console.error("Error submitting quiz:", error);
      setErrorMessage(
        "There was an error submitting your quiz. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Wrapper component that adds the consistent layout to all views
  const PageWrapper: React.FC<{ children: React.ReactNode }> = ({
    children,
  }) => (
    <div className="flex flex-col items-center min-h-screen bg-background">
      <PublicNavBar />
      <div className="w-full max-w-5xl px-4 py-8 md:py-12">{children}</div>
      <div className="mt-auto w-full">
        <PublicFooter />
      </div>
    </div>
  );

  if (showIntro) {
    return (
      <PageWrapper>
        <div className="relative w-full mt-8">
          <img
            src={testimonial1}
            alt="Testimonial 1"
            className="absolute z-10 w-20 h-20 md:w-24 md:h-24 rounded-full object-cover shadow-lg -top-10 left-6 md:left-8"
          />
          <img
            src={testimonial2}
            alt="Testimonial 2"
            className="absolute z-10 w-20 h-20 md:w-24 md:h-24 rounded-full object-cover shadow-lg -top-8 right-6 md:right-8"
          />
          <Card className="w-full p-8 md:p-12 text-center bg-white border-secondary/50 mt-12 rounded-xl">
            <CardHeader className="pb-8">
              <h2 className="text-3xl md:text-4xl">
                Should your nanny be a{" "}
                <span className="text-primary font-inherit">
                  contractor
                </span>{" "}
                or an{" "}
                <span className="text-primary font-inherit">
                  employee?
                </span>
              </h2>
            </CardHeader>
            <CardContent>
              <p className="text-lg text-muted-foreground mb-10 md:mb-12">
                Stay compliant and avoid fines. Take our quick quiz to determine
                whether your team member should be classified as a contractor or
                employee.
              </p>
              <Button
                size="lg"
                className="w-full md:w-auto px-10 py-6 text-lg mb-10 md:mb-12"
                onClick={() => setShowIntro(false)}
              >
                Start now
              </Button>
              <div className="space-y-3 text-left mx-auto max-w-md text-base md:text-lg">
                <div className="flex items-center">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3" />
                  <span>It takes 2 minutes</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3" />
                  <span>It's 100% free</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3" />
                  <span>See the potential risks</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </PageWrapper>
    );
  }

  if (isSubmitting) {
    return (
      <PageWrapper>
        <Card className="w-full p-6 border-secondary/50">
          <div className="flex flex-col items-center justify-center space-y-4 py-12">
            <div className="animate-spin h-8 w-8 border-2 border-secondary rounded-full border-t-transparent"></div>
            <p>Submitting your results...</p>
          </div>
        </Card>
      </PageWrapper>
    );
  }

  if (errorMessage) {
    return (
      <PageWrapper>
        <Card className="w-full p-6 border-secondary/50">
          <div className="text-center py-12">
            <h2 className="text-2xl mb-4">Error</h2>
            <p className="mb-4 text-destructive">{errorMessage}</p>
            <Button
              onClick={() => {
                if (userInfo) {
                  handleUserInfoSubmit(userInfo);
                } else {
                  // Fallback if userInfo is somehow null, though unlikely in this flow
                  setShowInfoForm(true);
                }
              }}
            >
              Try Again
            </Button>
          </div>
        </Card>
      </PageWrapper>
    );
  }

  if (submissionComplete) {
    const resultText =
      quizResult?.classification === "contractor"
        ? "Likely Independent Contractor"
        : quizResult?.classification === "employee"
          ? "Likely Employee (PAYE)"
          : "Borderline - further review recommended";

    return (
      <PageWrapper>
        <Card className="w-full p-6 border-secondary/50">
          <div className="text-center py-12">
            <h2 className="text-2xl mb-4">Thank You!</h2>
            <p className="mb-2">Your quiz has been submitted successfully.</p>
            <p>A detailed analysis will be emailed to {userInfo?.email}.</p>
          </div>
        </Card>
      </PageWrapper>
    );
  }

  if (showInfoForm) {
    return (
      <PageWrapper>
        <UserInfoForm onSubmit={handleUserInfoSubmit} />
      </PageWrapper>
    );
  }

  const currentQuestion = QUESTIONS[currentQuestionIndex];
  const progressPercentage =
    ((currentQuestionIndex + 1) / QUESTIONS.length) * 100;

  return (
    <PageWrapper>
      <Card className="w-full bg-white border-secondary/50">
        <CardContent className="pt-8 pb-6 px-6 md:px-8">
          <Button
            variant="link"
            className="mb-8 p-0 h-auto font-normal text-base text-primary"
            onClick={handleRestartQuiz}
          >
            ← Restart quiz
          </Button>

          <h2 className="text-3xl text-center mb-10">{currentQuestion.text}</h2>

          <div className="space-y-5">
            <div
              className="border border-secondary/30 rounded-lg p-6 md:p-8 flex items-start gap-4 cursor-pointer hover:bg-secondary/10 text-base md:text-lg"
              onClick={() => handleAnswer(currentQuestion.id, "Contractor")}
            >
              <span>{currentQuestion.contractorOption}</span>
            </div>

            <div
              className="border border-secondary/30 rounded-lg p-6 md:p-8 flex items-start gap-4 cursor-pointer hover:bg-secondary/10 text-base md:text-lg"
              onClick={() => handleAnswer(currentQuestion.id, "Employee")}
            >
              <span>{currentQuestion.employeeOption}</span>
            </div>
          </div>
        </CardContent>

        <CardFooter className="flex justify-between items-center pt-6 pb-8 px-6 md:px-8">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            className="text-base py-2 px-4"
          >
            Previous question
          </Button>

          <div className="flex items-center gap-3">
            <div className="w-36 h-2.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
            <span className="text-base text-muted-foreground whitespace-nowrap">
              {currentQuestionIndex + 1} / {QUESTIONS.length}
            </span>
          </div>
        </CardFooter>
      </Card>
    </PageWrapper>
  );
};

export default ContractorQuiz;
